import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectToasts } from '../../store/slices/toastSlice';
import { selectToastConfig, selectMaxToasts } from '../../store/slices/toastConfigSlice';
import Toast from './Toast';
import styles from './ToastContainer.module.css';

/**
 * Toast Container component that displays all active toasts
 */
const ToastContainer = () => {
  // Get all toasts from Redux store
  const toasts = useSelector(selectToasts);
  const config = useSelector(selectToastConfig);
  const maxToasts = useSelector(selectMaxToasts);

  // Apply configuration to the toast list
  const visibleToasts = useMemo(() => {
    // Limit number of toasts based on maxToasts setting
    let filteredToasts = [...toasts];
    
    // Show newest on top or bottom based on config
    filteredToasts.sort((a, b) => {
      const sortOrder = config.newestOnTop ? -1 : 1;
      return sortOrder * (b.createdAt - a.createdAt);
    });
    
    // Limit to maximum number of toasts
    if (filteredToasts.length > maxToasts) {
      filteredToasts = filteredToasts.slice(0, maxToasts);
    }
    
    return filteredToasts;
  }, [toasts, config.newestOnTop, maxToasts]);

  if (visibleToasts.length === 0) return null;

  // Position classes based on config
  const positionClass = styles[config.position] || styles['top-right'];
  const rtlClass = config.rtl ? styles.rtl : '';

  return (
    <div 
      className={`${styles.container} ${positionClass} ${rtlClass}`} 
      aria-live="polite"
    >
      {visibleToasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          duration={toast.duration}
          pauseOnHover={config.pauseOnHover}
          dismissible={config.dismissible}
        />
      ))}
    </div>
  );
};

export default ToastContainer;
